/**
 * Standard error response interface for API errors
 */
export interface ErrorResponse {
    statusCode: number;
    errors: string[];
    message: string;
}

/**
 * Alternative error response format
 */
export interface ErrorResponseObject {
    error: string;
    message: string;
}

/**
 * Type guard to check if an error is an ErrorResponse
 *
 * @param error - Unknown error object
 * @returns True if error is an ErrorResponse
 */
export function isApiErrorResponse(error: unknown): error is ErrorResponse {
    return (
        error !== null && typeof error === "object" && "statusCode" in error && "errors" in error && "message" in error
    );
}

/**
 * Type guard to check if an error is an object with a statusCode property
 * @param error
 */
export function isStatusCodeError(error: unknown): error is { statusCode: number } {
    return error !== null && typeof error === "object" && "statusCode" in error;
}

/**
 * Type guard to check if an error is an ErrorResponseObject
 *
 * @param error - Unknown error object
 * @returns True if error is an ErrorResponseObject
 */
export function isErrorResponseObject(error: unknown): error is ErrorResponseObject {
    return error !== null && typeof error === "object" && "error" in error && "message" in error;
}

/**
 * Type guard to check if an error is an ApiError
 * @param error - Unknown error object
 */
export function isApiError(error: unknown): error is ApiError {
    return error instanceof ApiError;
}

/**
 * Extracts a user-friendly error message from an API error.
 *
 * This function handles various error formats and provides consistent,
 * user-friendly error messages based on HTTP status codes.
 *
 * @param error - The error to extract a message from
 * @returns User-friendly error message
 */
export function getApiErrorMessage(error: unknown): string {
    console.log("getApiErrorMessage:", error);
    let statusCode = 0;
    if (isApiError(error)) {
        if (typeof error.context === "string") {
            return error.context;
        }
        if (error.context !== null && typeof error.context === "object") {
            if ("detail" in error.context && typeof error.context.detail === "string") {
                return error.context.detail;
            }
            if ("message" in error.context && typeof error.context.message === "string") {
                return error.context.message;
            }
        }
        statusCode = error.statusCode;
    } else if (error !== null && typeof error === "object" && "detail" in error && typeof error.detail === "string") {
        return error.detail;
    } else if (isApiErrorResponse(error)) {
        statusCode = error.statusCode;
    } else if (isErrorResponseObject(error)) {
        switch (error.error) {
            case "Bad Request":
                statusCode = 400;
                break;
            case "Unauthorized":
                statusCode = 401;
                break;
            case "Forbidden":
                statusCode = 403;
                break;
            case "Not Found":
                statusCode = 404;
                break;
            case "Internal Server Error":
                statusCode = 500;
                break;
        }
    } else if (isStatusCodeError(error)) {
        statusCode = error.statusCode;
    }

    if (statusCode !== 0) {
        switch (statusCode) {
            case 400:
                return "Bad request. Contact the Graduate College.";
            case 401:
                return "Unauthorized. Please log in and try again.";
            case 403:
                return "Forbidden. You do not have permission to perform this action.";
            case 404:
                return "Not found. The requested resource could not be found.";
            case 500:
                return "Internal server error. Contact the Graduate College.";
            default:
                return `Error ${statusCode}: Contact the Graduate College.`;
        }
    }

    // TypeError is what fetch throws when the request fails due to a network error
    if (error instanceof Error && error.name === "TypeError") {
        return "A network error occurred. Please check your internet connection and try again.";
    }

    return "Unknown error, contact the Graduate College.";
}

/**
 * Custom error class with additional context
 *
 * @example
 * ```typescript
 * throw new ApiError("Failed to save record", 500, "abc123")
 * ```
 */
export class ApiError extends Error {
    statusCode: number;
    context: any;

    constructor(message: string, statusCode: number = 500, context?: any) {
        super(message);
        this.name = "ApiError";
        this.statusCode = statusCode;
        this.context = context;
    }
}
